import { Injectable } from '@angular/core';
import { UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import differenceInDays from 'date-fns/differenceInDays';
import startOfWeek from 'date-fns/startOfWeek';
import isWithinInterval from 'date-fns/isWithinInterval';
import differenceInMilliseconds from 'date-fns/differenceInMilliseconds';
import addDays from 'date-fns/addDays';
import addHours from 'date-fns/addHours';
import format from 'date-fns/format';
import startOfDay from 'date-fns/startOfDay';
import differenceInHours from 'date-fns/differenceInHours';

import { ServerTimeResponse } from '@shared/core';
import addMinutes from 'date-fns/addMinutes';
import subMinutes from 'date-fns/subMinutes';
import subMilliseconds from 'date-fns/subMilliseconds';
import endOfWeek from 'date-fns/endOfWeek';
import { WfmBaseApiService } from '@shared/core/services/wfm-base-api.service';
import isAfter from 'date-fns/isAfter';
import addMilliseconds from 'date-fns/addMilliseconds';

const MOSCOW_TIMEZONE_OFFSET = 3;

const BACKDATING_PERIOD = 2; // days

export interface IServerTimeResponse {
  success: boolean;
  timestamp: number;
  timezone: string;
  offset: number;
}

@Injectable({
  providedIn: 'root',
})
export class CalendarService {
  private userTimezone = new BehaviorSubject<string>('Europe/Moscow');
  private userTimezoneOffset = new BehaviorSubject<number>(3 * 60);
  /**
   * Timezone offset in hours (relative to UTC)
   * Example: -3 for MSK (+3)
   * @var number
   */
  private timezoneOffset = 0 - MOSCOW_TIMEZONE_OFFSET;

  userTimezoneOffset$ = this.userTimezoneOffset.asObservable();
  currentDay$ = this.userTimezoneOffset$.pipe(map(() => this.getCurrentDay()));
  currentWeek$ = this.userTimezoneOffset$.pipe(
    map(timezoneOffset => startOfWeek(subMinutes(new Date(), timezoneOffset), { weekStartsOn: 1 })),
  );

  constructor(private wfmBaseApiService: WfmBaseApiService) {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loadServerTimeData(): Promise<any> {
    return new Promise(resolve => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.getServerTime().subscribe((response: any) => {
        if (response) {
          this.timezoneOffset = new Date().getTimezoneOffset() / 60 + response.offset;
        }
        resolve(true);
      });
    });
  }

  getCurrentDay(): Date {
    return startOfDay(this.utcDateTime(new Date()));
  }

  setTimezoneOffset(serverTime: Date | string): void {
    const serverDate = new Date(serverTime);
    let hoursOffset;
    if (serverTime) {
      hoursOffset = Math.round(
        differenceInMilliseconds(
          new Date(
            serverDate.getUTCFullYear(),
            serverDate.getUTCMonth(),
            //
            serverDate.getDate(),
            serverDate.getHours(),
            serverDate.getMinutes(),
            serverDate.getSeconds(),
            serverDate.getMilliseconds(),
          ),
          new Date(
            serverDate.getUTCFullYear(),
            serverDate.getUTCMonth(),
            serverDate.getUTCDate(),
            serverDate.getUTCHours(),
            serverDate.getUTCMinutes(),
            serverDate.getUTCSeconds(),
            serverDate.getUTCMilliseconds(),
          ),
        ) / 60000,
      );

      hoursOffset = -hoursOffset;
    } else {
      hoursOffset = new Date().getTimezoneOffset();
    }

    this.timezoneOffset = hoursOffset / 60;
  }

  getTimezoneOffset(): number {
    return this.timezoneOffset;
  }

  getServerTimezoneOffset(): number {
    return MOSCOW_TIMEZONE_OFFSET;
  }

  /**
   * Change date by server time (without change timezone)
   *
   * Example (for timezone MSK+3):
   * 10:00 (MSK+4) to 09:00 (MSK+4)
   *  date: 10:00
   *  timezoneOffset: -4
   *  serverTimezoneOffset: 3
   */
  serverDateTime(date: Date | string): Date {
    return addMinutes(new Date(date), new Date(date).getTimezoneOffset() + this.getServerTimezoneOffset() * 60);
  }

  toSerializeValue(date: Date): string {
    return format(date, "yyyy-MM-dd'T'HH:mm:ss+03:00");
  }

  userDateTime(date: Date | string, timeZone: string): Date {
    return new Date(new Date(date).toLocaleString('en-US', { timeZone: timeZone }));
  }

  getUtcDate(date: Date, timezone: string): Date {
    const realLocalTimeMod = addMinutes(new Date(date), -new Date(date).getTimezoneOffset());

    const differ = this.userDateTime(realLocalTimeMod, timezone).getTime() - new Date(date).getTime();

    return addMilliseconds(date, -differ);
  }

  getUtcDateString(date: Date, timezone: string): Date {
    const differ = this.userDateTime(date, timezone).getTime() - new Date(date).getTime();

    return addMilliseconds(date, -differ);
  }
  /**
   * Change date by utc time (without change timezone)
   *
   * Example:
   * 10:00 (MSK+4) to 06:00 (MSK+4)
   *  date: 10:00
   *  timezoneOffset: -4
   */
  utcDateTime(date: Date | string): Date {
    return addMinutes(new Date(date), this.timezoneOffset * 60);
  }

  /**
   * Convert ServerDateTime to UTCDateTime (without change timezone)
   *
   * Example:
   * 10:00 (MSK+4) to 07:00 (MSK+4)
   *  date: 10:00
   *  serverTimezoneOffset: 3
   */
  toUTCFromServerDateTime(date: Date | string): Date {
    return addHours(new Date(date), -this.getServerTimezoneOffset());
  }

  /**
   * Convert UTCDateTime to ServerDateTime (without change timezone)
   *
   * Example:
   * 10:00 (MSK+4) to 13:00 (MSK+4)
   *  date: 10:00
   *  serverTimezoneOffset: 3
   */
  toServerDateTimeFromUTC(date: Date | string): Date {
    return addHours(new Date(date), this.getServerTimezoneOffset());
  }

  /**
   * Convert date to ServerDateTime (without change timezone)
   * Add offset in hours between server timezone and local
   *
   * Example (for timezone MSK+3):
   * 10:00 (MSK+4) to 11:00 (MSK+4)
   *  date: 10:00
   *  utcDate: 06:00
   *  serverDateUtc: 07:00
   */
  toServerDateTime(date: Date | string): Date {
    const utcDate = this.utcDateTime(date);
    const serverDateUtc = this.toUTCFromServerDateTime(date);
    return addHours(new Date(date), differenceInHours(serverDateUtc, utcDate));
  }

  /**
   * Convert date to UTCDateTime
   * Add total offset in hours between local timezone and server
   *
   * Example (for timezone MSK+3):
   * 10:00 (MSK+4) to 03:00 (MSK+4)
   *  date: 10:00
   *  timezoneOffset: -4
   *  serverTimezoneOffset: 3
   */
  toUTCDateTime(date: Date | string): Date {
    return addHours(new Date(date), this.timezoneOffset - this.getServerTimezoneOffset());
  }

  toUTCFormatFromServerDateTime(date: Date | string): string {
    return this.toISOStringSafe(this.toUTCFromServerDateTime(date));
  }

  toUTCFormat(date: Date | string): string {
    return this.toISOStringSafe(this.utcDateTime(date));
  }

  /**
   * Return ISO string for date without apply timeout offset
   */
  toISOStringSafe(date: Date | string | number): string {
    return format(new Date(date), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
  }

  dayPeriodValidator(start: string, end: string): (group: UntypedFormGroup) => ValidationErrors | null {
    return (group: UntypedFormGroup): ValidationErrors | null => {
      if (!group.value) {
        return null;
      }

      const from = group.get(start)?.value || null;
      const to = group.get(end)?.value || null;
      if (from && to) {
        const fromDate = new Date(from);
        const toDate = new Date(to);
        const diff = differenceInDays(startOfDay(fromDate), startOfDay(toDate));

        return diff <= 0 ? null : { invalidDatePeriod: true };
      }
      return null;
    };
  }

  timePeriodValidator(start: string, end: string): (group: UntypedFormGroup) => ValidationErrors | null {
    return (group: UntypedFormGroup): ValidationErrors | null => {
      if (!group.value) {
        return null;
      }
      const from = group.get(start)?.value || null;
      const to = group.get(end)?.value || null;
      const date = this.utcDateTime(new Date());

      if (from && to && date) {
        const fromDate = `${format(date, 'yyyy-MM-dd')}T${from}Z`;
        const toDate = `${format(date, 'yyyy-MM-dd')}T${to}Z`;
        const diff = differenceInMilliseconds(this.utcDateTime(toDate), this.utcDateTime(fromDate));

        return diff > 0 ? null : { invalidTimePeriod: true };
      }
      return null;
    };
  }

  isWithinRange(start: Date | string, end: Date | string, time: Date | string): boolean {
    const startDateInterval = this.serverDateTime(start);
    const endDateInterval = this.serverDateTime(end);
    endDateInterval.setDate(startDateInterval.getDate());

    return isWithinInterval(new Date(time), {
      start: startDateInterval,
      end: endDateInterval,
    });
  }

  isPreviousWeek(date: Date): boolean {
    const start = startOfWeek(new Date(), { weekStartsOn: 1 });
    // previous week, if days between Monday and date less than zero
    return differenceInDays(startOfDay(start), date) < 0;
  }

  isPreviousUTCDay(date: Date): boolean {
    // previous day, if days between current day (00:00:00) and utc date (00:00:00) more than zero
    return differenceInDays(startOfDay(this.utcDateTime(new Date())), startOfDay(date)) > 0;
  }

  isInThreeDaysInterval(date: Date): boolean {
    // checks if the given date is within the given interval of 3 days
    return !isAfter(this.getFirstAvailableDate(), startOfDay(date));
  }

  getFirstAvailableDate(): Date {
    return addDays(startOfDay(this.utcDateTime(new Date())), -BACKDATING_PERIOD);
  }

  getWeekDays(selectedDate: Date): Date[] {
    const fromDate = startOfWeek(selectedDate, { weekStartsOn: 1 });
    const viewDays = [];
    for (let i = 0; i < 7; i++) {
      // eslint-disable-next-line functional/immutable-data
      viewDays.push(addDays(fromDate, i));
    }
    return viewDays;
  }

  getZonedTime(date: Date): Date {
    /*
     JS inverts timezone offset, e.g. "usual" offset for Moscow +3 hours or +180 minutes but in JS terms it's -180 minutes
     It's easier to think about offset in terms of "usual" timezone offset so that we use inverted form browser's offset
     */
    const browserTimezoneOffset = -date.getTimezoneOffset();
    const userTimezoneOffset = this.getUserTimezoneOffset();

    return addMinutes(subMinutes(date, browserTimezoneOffset), userTimezoneOffset);
  }

  /**
   * Return 00:00 - 00:00
   */
  getUtcDayIntervalFromServerDateTime(localStartDate: Date): Interval {
    const localEndDate = addDays(localStartDate, 1);

    return {
      start: this.toUTCFromServerDateTime(localStartDate),
      end: this.toUTCFromServerDateTime(localEndDate),
    };
  }

  /**
   * Return 00:00 - 23:59
   */
  getServerDayIntervalFromUtc(utcStartDate: Date): Interval {
    const serverEndDate = subMilliseconds(addDays(utcStartDate, 1), 1);

    return {
      start: this.toUTCFromServerDateTime(utcStartDate),
      end: this.toUTCFromServerDateTime(serverEndDate),
    };
  }

  /**
   * Return 00:00 - 23:59
   */
  getDayInterval(startDate: Date): Interval {
    return {
      start: startDate,
      end: subMilliseconds(addDays(startDate, 1), 1),
    };
  }

  getWeekInterval(start: Date): Interval {
    return {
      start: start,
      end: subMilliseconds(endOfWeek(start, { weekStartsOn: 1 }), 1),
    };
  }

  getUserStartOfDay(day: Date): Date {
    return CalendarService.getStartOfDayForTimezone(day, this.getUserTimezoneOffset());
  }

  getUtcTime(date: Date): Date {
    /*
     JS inverts timezone offset, e.g. "usual" offset for Moscow +3 hours or +180 minutes but in JS terms it's -180 minutes
     It's easier to think about offset in terms of "usual" timezone offset so that we use inverted form browser's offset
     */
    const browserTimezoneOffset = -date.getTimezoneOffset();
    const userTimezoneOffset = this.getUserTimezoneOffset();

    return subMinutes(addMinutes(date, browserTimezoneOffset), userTimezoneOffset);
  }

  getTimeWithOffset(utcTime: Date): Date {
    return addMinutes(utcTime, this.getUserTimezoneOffset());
  }

  getUserTimezone(): string {
    return this.userTimezone.value;
  }

  getUserTimezoneOffset(): number {
    return this.userTimezoneOffset.value;
  }

  private static getStartOfDayForTimezone(day: Date, timezoneOffset: number): Date {
    const date = addMinutes(day, timezoneOffset).toISOString().slice(0, 10);
    const startOfDay = new Date(date);

    return subMinutes(startOfDay, timezoneOffset);
  }

  private getServerTime(): Observable<ServerTimeResponse> {
    return this.wfmBaseApiService.getServerTime();
  }
}
